import { useQuery } from '@apollo/client'
import Button from '@mui/material/Button'
import moment from 'moment'
import React, { useCallback, useMemo, useRef } from 'react'
import styled from 'styled-components'

import ComparisonBarChart from 'pared/charts/ComparisonBarChart'
import useLocationInfo from 'pared/components/LocationInfo/hooks/useLocationInfo'
import Spin from 'pared/components/basicUi/spin'
import Table, {
  IPropsType as ITablePropsType,
} from 'pared/components/basicUi/table'
import COLORS from 'pared/constants/colors'
import { MOBILE_WIDTH } from 'pared/constants/styles'
import useGetDateRange from 'pared/hooks/useGetDateRange'

import { IQueryFilter } from '../../type'
import {
  IListLocationDetailsV2Response,
  IListLocationDetailsV2Variables,
  ILocationDetailsGroupType,
  LIST_LOCATION_DETAILS_V2,
} from './gql'
import useAccountingSalesReportColumns from './hooks/useAccountingSalesReportColumns'
import useAccountingSalesReportDatasource, {
  IAccountingSalesReportData,
} from './hooks/useAccountingSalesReportDatasource'
import useGetDateInfo from './hooks/useGetDateInfo'

interface IAiBlackboxProps {
  startDate?: string
  endDate?: string
  queryFilter: IQueryFilter | null
}

const Container = styled.div`
  font-family: Lexend-Regular;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  padding: 20px;

  @media ${MOBILE_WIDTH} {
    margin: 20px 0px 0px;
    padding: 20px 30px;
    border: 1px solid;
    overflow: scroll hidden;
  }
`

const rowStyle = `
  &:nth-child(even) {
    background-color: ${COLORS.Porcelain};
  }
`

const AccountingSalesReportTable = styled<
  React.ComponentType<ITablePropsType<IAccountingSalesReportData>>
>(Table)`
  width: 100%;
  max-width: 100%;

  /* Gray background for header columns */
  thead tr th {
    background-color: #e0e0e0;
  }
`

const ChartContainer = styled.div`
  margin-top: 20px;
  padding: 20px;
  background-color: #1a1a1a;
  border-radius: 8px;
  border: 1px solid #333333;
`

const DateInfoContainer = styled.div`
  margin-bottom: 20px;
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
`

const DateInfoRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;

  &:last-child {
    margin-bottom: 0;
  }
`

const DateLabel = styled.span`
  font-weight: 600;
  margin-right: 8px;
  color: #333;
`

const DateValue = styled.span`
  color: #666;
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 16px;
`

const ChartButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`

const SubHeader = styled.div`
  padding: 60px 0px 20px 0px;
  display: flex;
  width: 1200px;
  justify-content: space-between;

  @media ${MOBILE_WIDTH} {
    display: block;
    width: 100%;
    margin: 60px 0px 0px;
  }

  h1 {
    margin: 0px;
    font-family: Lexend-SemiBold;
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
  }
`

const AccountingSalesReport: React.FC<IAiBlackboxProps> = ({
  startDate,
  endDate,
  queryFilter,
}) => {
  const locationId = queryFilter?.locationIds?.[0] || 0
  const locationInfo = useLocationInfo(locationId)
  const dateRange = useGetDateRange()
  const selectedYear = dateRange?.year || moment().year()
  const chartRef = useRef<HTMLDivElement>(null)

  // Fetch location details for dates
  const { data: locationDetailsData, loading: locationDetailsLoading } =
    useQuery<IListLocationDetailsV2Response, IListLocationDetailsV2Variables>(
      LIST_LOCATION_DETAILS_V2,
      {
        variables: {
          iStartDate: '1900-01-01',
          iEndDate: '9999-12-31',
          iFilter: {
            location_ids: [locationId],
          },
        },
        skip: !startDate || !endDate || !locationId,
      },
    )

  // Parse comp store date from locationGroups
  const { openingDate, compStoreDate } = useMemo(() => {
    if (!locationDetailsData?.listLocationDetailsV2?.nodes?.[0]) {
      return { openingDate: null, compStoreDate: null }
    }

    const node = locationDetailsData.listLocationDetailsV2.nodes[0]
    const openingDate =
      node.locationGroups.find(
        (group: ILocationDetailsGroupType) =>
          group.type === 'Comp or Non-Comp' && group.name === 'Non-Comp',
      )?.start_date || null
    const compStoreDate =
      node.locationGroups.find(
        (group: ILocationDetailsGroupType) =>
          group.type === 'Comp or Non-Comp' && group.name === 'Comp',
      )?.start_date || null

    return { openingDate, compStoreDate }
  }, [locationDetailsData])

  const { compDateData, loading: compDateLoading } = useGetDateInfo({
    queryDate: openingDate || '',
    currentYear: selectedYear,
  })

  const { dataSource, loading } = useAccountingSalesReportDatasource({
    locationId,
    startDate,
    endDate,
    selectedYear,
    compDateData,
  })

  const columns = useAccountingSalesReportColumns(selectedYear)

  // Prepare chart data - extract weekly sales data for comparison
  const chartData = useMemo(() => {
    if (!dataSource.length)
      return { xAxisData: [], dataset1Data: [], dataset2Data: [] }

    // Use all weeks for the chart
    const xAxisData = dataSource.map((row) => row.week)
    const dataset1Data = dataSource.map((row) => row.actualSales)
    const dataset2Data = dataSource.map((row) => row.actualSalesPastYear)

    return {
      xAxisData,
      dataset1Data,
      dataset2Data,
    }
  }, [dataSource])

  const chartOptions = useMemo(() => {
    const currentYear = selectedYear
    const previousYear = selectedYear - 1

    return {
      width: 1200,
      height: 400,
      title: `Bibibop - ${
        locationInfo?.displayName || ''
      } - Sales ${currentYear} vs ${previousYear}`,
      xAxisLabel: 'Week',
      yAxisLabel: 'Sales ($)',
      dataset1Label: currentYear.toString(),
      dataset2Label: previousYear.toString(),
      dataset1Color: '#F44336', // Red for current year
      dataset2Color: '#4CAF50', // Green for previous year
    }
  }, [selectedYear, locationInfo])

  // CSV download functionality
  const downloadCSV = useCallback(() => {
    // Prepare CSV header
    const headers = [
      'Week',
      'Weekend Date',
      'Month',
      `Monthly Sales ${selectedYear}`,
      `Actual ${selectedYear} Net Sales`,
      '% LW',
      `Monthly Sales for ${selectedYear - 1}`,
      `Actual ${selectedYear - 1} Net Sales`,
      'Comp % LY',
      `Budgeted Sales for ${selectedYear}`,
      `Cumulative Sales for ${selectedYear}`,
      `Cumulative Sales for ${selectedYear - 1}`,
      'Monthly Comp % LY',
      'YTD Comp % LY (Cumulative)',
    ]

    // Helper function to format values for CSV
    const formatValue = (
      value: any,
      isPercentage = false,
      isCurrency = false,
    ) => {
      if (value === undefined || value === null || value === ' ') return ''
      if (value === '-') return '-'
      if (isPercentage) return `${value.toFixed(2)}%`
      if (isCurrency) return value.toFixed(2)
      return value
    }

    // Prepare CSV rows
    const rows = dataSource.map((row) => {
      const showMonthData = row.isLastWeekOfMonth
      const showPastYearData = row.showPastYearData

      return [
        row.week,
        `'${row.formattedWeekendDate}`, // Add apostrophe to prevent Excel date conversion
        showMonthData ? row.month : '',
        showMonthData ? formatValue(row.monthlySales, false, true) : '',
        formatValue(row.actualSales, false, true),
        formatValue(row.percentLW, true),
        showMonthData && showPastYearData
          ? formatValue(row.monthlySalesPastYear, false, true)
          : showMonthData && !showPastYearData
          ? '-'
          : '',
        showPastYearData
          ? formatValue(row.actualSalesPastYear, false, true)
          : '-',
        showPastYearData ? formatValue(row.compPercentLY, true) : '-',
        formatValue(row.budgetedSales, false, true),
        formatValue(row.cumulativeSales, false, true),
        showPastYearData
          ? formatValue(row.cumulativeSalesPastYear, false, true)
          : '-',
        showMonthData && showPastYearData
          ? formatValue(row.ytdCompLYMonthly, true)
          : showMonthData && !showPastYearData
          ? '-'
          : '',
        showMonthData && showPastYearData
          ? formatValue(row.ytdCompLYCumulative, true)
          : showMonthData && !showPastYearData
          ? '-'
          : '',
      ]
    })

    // Add date information at the beginning
    const dateInfo = []
    if (openingDate) {
      dateInfo.push([
        'Date of Opening',
        `'${moment(openingDate).format('M/D/YYYY')}`,
      ])
    }
    if (compStoreDate) {
      dateInfo.push([
        'Comp Store Date',
        `'${moment(compStoreDate).format('M/D/YYYY')}`,
      ])
    }
    if (dateInfo.length > 0) {
      dateInfo.push([]) // Empty row
    }

    // Combine all data
    const csvContent = [...dateInfo, headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n')

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    const fileName = `accounting_sales_report_${
      locationInfo?.displayName || 'location'
    }_${moment().format('YYYYMMDD')}.csv`

    link.setAttribute('href', url)
    link.setAttribute('download', fileName)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [dataSource, openingDate, compStoreDate, selectedYear, locationInfo])

  // Chart CSV download functionality
  const downloadChartCSV = useCallback(() => {
    const headers = [
      'Week',
      `Sales ${selectedYear}`,
      `Sales ${selectedYear - 1}`,
    ]

    const rows = chartData.xAxisData.map((week, index) => [
      week,
      chartData.dataset1Data[index].toFixed(2),
      chartData.dataset2Data[index].toFixed(2),
    ])

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    const fileName = `chart_data_${
      locationInfo?.displayName || 'location'
    }_${moment().format('YYYYMMDD')}.csv`

    link.setAttribute('href', url)
    link.setAttribute('download', fileName)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [chartData, selectedYear, locationInfo])

  return (
    <Container>
      <Spin spinning={loading || locationDetailsLoading || compDateLoading}>
        {(openingDate || compStoreDate) && (
          <DateInfoContainer>
            {openingDate && (
              <DateInfoRow>
                <DateLabel>Date of Opening:</DateLabel>
                <DateValue>{moment(openingDate).format('M/D/YYYY')}</DateValue>
              </DateInfoRow>
            )}
            {compStoreDate && (
              <DateInfoRow>
                <DateLabel>Comp Store Date:</DateLabel>
                <DateValue>
                  {moment(compStoreDate).format('M/D/YYYY')}
                </DateValue>
              </DateInfoRow>
            )}
          </DateInfoContainer>
        )}
        <ButtonContainer>
          <Button onClick={() => downloadCSV()} variant="contained">
            Download as CSV
          </Button>
        </ButtonContainer>
        <AccountingSalesReportTable
          dataSource={dataSource}
          columns={columns}
          rowStyle={rowStyle}
          rowKey="week"
          pagination={{ pageSize: 55 }}
        />
        {chartData.xAxisData.length > 0 && (
          <>
            <SubHeader>
              <h1>Sales Comparison Chart</h1>
            </SubHeader>
            <ChartButtonContainer>
              <Button onClick={downloadChartCSV} variant="contained">
                Download as CSV
              </Button>
            </ChartButtonContainer>
            <ChartContainer ref={chartRef}>
              <ComparisonBarChart
                xAxisData={chartData.xAxisData}
                dataset1Data={chartData.dataset1Data}
                dataset2Data={chartData.dataset2Data}
                options={chartOptions}
              />
            </ChartContainer>
          </>
        )}
      </Spin>
    </Container>
  )
}

export default AccountingSalesReport
